// Design System - Restaurant Management System
// Enhanced with comprehensive responsive design utilities

export const theme = {
  colors: {
    primary: '#1B5E20', // Dark Green
    primaryLight: '#2E7D32',
    primaryDark: '#0D3E10',
    secondary: '#F5F5F5', // Light Gray
    secondaryDark: '#E0E0E0',
    accent: '#FF6B35', // Orange
    accentTeal: '#00897B', // Teal
    background: '#FAFAFA',
    white: '#FFFFFF',
    text: '#212121',
    textSecondary: '#757575',
    textLight: '#9E9E9E',
    border: '#E0E0E0',
    // Status Colors
    status: {
      pending: '#FF6B35', // Orange
      preparing: '#1976D2', // Blue
      ready: '#2E7D32', // Green
      outOfStock: '#D32F2F', // Red
    },
    // Semantic colors for better UI
    error: '#D32F2F',
    warning: '#F57C00',
    success: '#2E7D32',
    info: '#1976D2',
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      extraBold: 800,
    },
    fontSize: {
      xs: '0.625rem',    // 10px
      sm: '0.75rem',     // 12px
      base: '0.875rem',  // 14px
      lg: '1rem',        // 16px
      xl: '1.125rem',    // 18px
      '2xl': '1.25rem',  // 20px
      '3xl': '1.5rem',   // 24px
      '4xl': '1.75rem',  // 28px
      '5xl': '2rem',     // 32px
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
      loose: 2,
    },
    // Responsive typography scales
    responsive: {
      // Mobile-first approach
      h1: {
        mobile: '1.5rem',     // 24px
        tablet: '1.75rem',    // 28px
        desktop: '2rem',     // 32px
      },
      h2: {
        mobile: '1.25rem',    // 20px
        tablet: '1.5rem',     // 24px
        desktop: '1.75rem',   // 28px
      },
      h3: {
        mobile: '1.125rem',   // 18px
        tablet: '1.25rem',    // 20px
        desktop: '1.5rem',   // 24px
      },
      body: {
        mobile: '0.75rem',    // 12px
        tablet: '0.875rem',  // 14px
        desktop: '0.875rem', // 14px
      },
    },
  },
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
    '4xl': '6rem',   // 96px
    '5xl': '8rem',   // 128px
  },
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    md: '0.5rem',    // 8px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
    '2xl': '1.5rem', // 24px
    full: '9999px',
  },
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  },
  breakpoints: {
    xs: '480px',   // Extra small devices (phones)
    sm: '640px',   // Small devices (large phones)
    md: '768px',   // Medium devices (tablets)
    lg: '1024px',  // Large devices (laptops)
    xl: '1280px',  // Extra large devices (desktops)
    '2xl': '1536px', // 2X Extra large devices (large desktops)
  },
  // Container max-widths for responsive layouts
  container: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
    full: '100%',
  },
  // Z-index scale for proper layering
  zIndex: {
    hide: -1,
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    modalBackdrop: 1300,
    modal: 1400,
    popover: 1500,
    tooltip: 1600,
    toast: 1700,
  },
  // Transitions and animations
  transitions: {
    duration: {
      fast: '150ms',
      base: '200ms',
      slow: '300ms',
      slower: '500ms',
    },
    easing: {
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
    default: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  // Grid system
  grid: {
    columns: 12,
    gutter: {
      xs: '0.5rem',
      sm: '1rem',
      md: '1.5rem',
      lg: '2rem',
    },
  },
  // Layout utilities
  layout: {
    headerHeight: '64px',
    footerHeight: '60px',
    sidebarWidth: {
      collapsed: '80px',
      expanded: '260px',
    },
  },
};

// Media query helper functions for styled-components
export const media = {
  // Mobile-first: min-width
  up: {
    xs: `@media (min-width: ${theme.breakpoints.xs})`,
    sm: `@media (min-width: ${theme.breakpoints.sm})`,
    md: `@media (min-width: ${theme.breakpoints.md})`,
    lg: `@media (min-width: ${theme.breakpoints.lg})`,
    xl: `@media (min-width: ${theme.breakpoints.xl})`,
    '2xl': `@media (min-width: ${theme.breakpoints['2xl']})`,
  },
  // Desktop-first: max-width
  down: {
    xs: `@media (max-width: ${Number.parseInt(theme.breakpoints.xs, 10) - 1}px)`,
    sm: `@media (max-width: ${Number.parseInt(theme.breakpoints.sm, 10) - 1}px)`,
    md: `@media (max-width: ${Number.parseInt(theme.breakpoints.md, 10) - 1}px)`,
    lg: `@media (max-width: ${Number.parseInt(theme.breakpoints.lg, 10) - 1}px)`,
    xl: `@media (max-width: ${Number.parseInt(theme.breakpoints.xl, 10) - 1}px)`,
    '2xl': `@media (max-width: ${Number.parseInt(theme.breakpoints['2xl'], 10) - 1}px)`,
  },
  // Between breakpoints
  between: (min, max) => 
    `@media (min-width: ${min}) and (max-width: ${Number.parseInt(max, 10) - 1}px)`,
  // Only specific breakpoint
  only: (breakpoint) => {
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
    const index = breakpoints.indexOf(breakpoint);
    if (index === 0) {
      return `@media (max-width: ${Number.parseInt(theme.breakpoints[breakpoints[index + 1]], 10) - 1}px)`;
    }
    if (index === breakpoints.length - 1) {
      return `@media (min-width: ${theme.breakpoints[breakpoint]})`;
    }
    return `@media (min-width: ${theme.breakpoints[breakpoint]}) and (max-width: ${Number.parseInt(theme.breakpoints[breakpoints[index + 1]], 10) - 1}px)`;
  },
};

// Responsive spacing helper
export const responsiveSpacing = {
  // Returns spacing that adapts to screen size
  container: {
    mobile: theme.spacing.md,
    tablet: theme.spacing.lg,
    desktop: theme.spacing.xl,
  },
  section: {
    mobile: theme.spacing.xl,
    tablet: theme.spacing['2xl'],
    desktop: theme.spacing['3xl'],
  },
};

// Responsive font size helper
export const responsiveFontSize = (size) => {
  const sizes = theme.typography.responsive[size];
  if (!sizes) return theme.typography.fontSize[size] || size;
  
  return `
    font-size: ${sizes.mobile};
    ${media.up.sm} {
      font-size: ${sizes.tablet};
    }
    ${media.up.lg} {
      font-size: ${sizes.desktop};
    }
  `;
};

